import type { WidenMediaElement } from '@hub/libs/widen-utils';
import { MediaElementType } from '@hub/libs/widen-utils';
import { useEffect, useRef } from 'react';
import { useBreakpoints } from '@hub/hooks/useBreakpoints';
import { useKeenSlider } from 'keen-slider/react';
import { useIsomorphicLayoutEffect } from '@hub/hooks/useIsomorphicLayoutEffect';
import classNames from 'classnames';
import styles from './ProductMediaCarousel.module.css';
import { WidenImage } from '@hub/common/Widen/WidenImage';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';

export interface ProductMediaThumbCarouselProps {
  activeIndex: number;
  media: WidenMediaElement[];
  onChange: (idx: number) => void;
}

export const ProductMediaThumbCarousel = ({ activeIndex, media, onChange }: ProductMediaThumbCarouselProps) => {
  const isMountedRef = useRef<boolean>(false);
  const { sm, lg } = useBreakpoints();
  const [carouselRef, carousel] = useKeenSlider<HTMLDivElement>({
    initial: activeIndex,
    slidesPerView: 3,
    spacing: 5,
    centered: true,
    loop: true,
    slideChanged: s => {
      if (!isMountedRef.current || s.details().relativeSlide === activeIndex) {
        return;
      }
      onChange(s.details().relativeSlide);
    },
  });

  useEffect(() => {
    if (carousel) {
      carousel.resize();
    }
  }, [lg, carousel]);

  useEffect(() => {
    if (carousel && carousel.details().relativeSlide !== activeIndex) {
      carousel.moveToSlideRelative(activeIndex, true);
    }
  }, [activeIndex, carousel]);

  useEffect(() => {
    if (carousel) {
      carousel.refresh();
    }
  }, [media, carousel]);

  useIsomorphicLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const width = lg ? 110 : 130;
  const height = lg ? 70 : 86;

  return (
    <div className="relative">
      <div
        ref={carouselRef}
        className={classNames('relative keen-slider', styles.thumbCarousel)}
        data-testid="product-image-carousel-thumbs"
      >
        {media.map((img, idx) => (
          <div
            className={classNames(
              'cursor-pointer border-gray-200 relative keen-slider__slide',
              activeIndex === idx ? 'border-4' : 'border',
              `number-slide${idx + 1}`,
              sm ? styles.thumbCarouselItemSm : styles.thumbCarouselItem
            )}
            onClick={() => carousel.moveToSlideRelative(idx, true)}
            key={`thumb-carousel-image-${img.shortCode}`}
          >
            <WidenImage
              width={width}
              height={height}
              alt={img.description || img.heading}
              layout="responsive"
              shortCode={img.shortCode}
              filename={img.filename}
              title={img.title}
              priority={img.priority}
              isVideo={img.type === MediaElementType.VIDEO}
            />
          </div>
        ))}
      </div>
      <div
        onClick={() => carousel.prev()}
        className={classNames('cursor-pointer absolute left-0', styles.thumbCarousel__arrow)}
      >
        <Icon size={7} id={IconID.ARROW_LEFT} color={IconColor.LIGHT_GRAY} />
      </div>
      <div
        onClick={() => carousel.next()}
        className={classNames('cursor-pointer absolute right-0', styles.thumbCarousel__arrow)}
      >
        <Icon size={7} id={IconID.ARROW_RIGHT} color={IconColor.LIGHT_GRAY} />
      </div>
    </div>
  );
};
